import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, Clock, Calendar } from 'lucide-react';
import { getCategories, getMaterials } from '../api';

const CategoryScreen = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [category, setCategory] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoriesData, materialsData] = await Promise.all([
          getCategories(),
          getMaterials()
        ]);
        
        const currentCategory = categoriesData.find(cat => cat.id === categoryId);
        setCategory(currentCategory);
        setMaterials(materialsData);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId]);
  
  const filteredAndSortedMaterials = useMemo(() => {
    if (!materials) return [];
    let filtered = materials.filter(material => 
      material.category === categoryId && 
      material.status === 'published'
    );

    // Поиск по названию
    if (searchQuery) {
      filtered = filtered.filter(material =>
        material.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Сортировка
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.date) - new Date(a.date);
      }
      if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });

    return filtered;
  }, [categoryId, searchQuery, sortBy, materials]);

  if (loading) {
    return <div className="p-4 text-center">Загрузка...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-500">Ошибка: {error}</div>;
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Категория не найдена
          </h2>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Вернуться назад
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Заголовок */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-md mx-auto p-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {category.name}
              </h1>
              <p className="text-sm text-gray-600">
                {filteredAndSortedMaterials.length} материалов
              </p>
            </div>
          </div>

          {/* Поиск */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск по названию..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Сортировка */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setSortBy('date')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                sortBy === 'date' 
                  ? 'bg-gold-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              По дате
            </button>
            <button
              onClick={() => setSortBy('title')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                sortBy === 'title' 
                  ? 'bg-gold-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              По названию
            </button>
          </div>
        </div>
      </div>

      {/* Список материалов */}
      <div className="max-w-md mx-auto p-4">
        {filteredAndSortedMaterials.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchQuery ? 'Материалы не найдены' : 'В этой категории пока нет материалов'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAndSortedMaterials.map((material) => (
              <div
                key={material.id}
                onClick={() => navigate(`/material/${material.id}`)}
                className="card p-4 cursor-pointer hover:shadow-lg transition-shadow duration-200"
              >
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <img
                      src={material.image}
                      alt={material.title}
                      className="w-16 h-16 rounded-lg object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/64x64?text=📄';
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-1 truncate">
                      {material.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {material.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {material.duration && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {material.duration}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(material.date).toLocaleDateString('ru-RU')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryScreen; 